import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Dropdown,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import { createTooltipProps } from '../utils/fieldTooltips';
import { validateFleetReminderForm, hasErrors, formatErrors } from '../utils/validation';
import MissingSelectOption from '../components/MissingSelectOption';

function FleetReminderModal({ fleetReminder, onClose, onSave }) {
  const [formData, setFormData] = useState({
    stock_no: '',
    service_name: '',
    due_date: '',
    due_meter: '',
    due_notes: '',
    inspection_name: '',
    last_test_date: '',
    last_test_no: '',
    during_hire_only: false,
  });

  const [inspectionNames, setInspectionNames] = useState([]);
  const [hireStockItems, setHireStockItems] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const fetchInspectionNames = async () => {
    try {
      const response = await axios.get('/api/inspection-tests/inspection-names');
      setInspectionNames(response.data);
    } catch (error) {
      console.error('Failed to fetch inspection names:', error);
    }
  };

  const fetchHireStockItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/hire-stock/stock-numbers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHireStockItems(response.data);
    } catch (error) {
      console.error('Failed to fetch hire stock items:', error);
    }
  };

  useEffect(() => {
    fetchInspectionNames();
    fetchHireStockItems();
  }, []);

  useEffect(() => {
    if (fleetReminder) {
      setFormData({
        stock_no: fleetReminder.stock_no || '',
        service_name: fleetReminder.service_name || '',
        due_date: fleetReminder.due_date ? fleetReminder.due_date.split('T')[0] : '',
        due_meter: fleetReminder.due_meter || '',
        due_notes: fleetReminder.due_notes || '',
        inspection_name: fleetReminder.inspection_name || '',
        last_test_date: fleetReminder.last_test_date
          ? fleetReminder.last_test_date.split('T')[0]
          : '',
        last_test_no: fleetReminder.last_test_no || '',
        during_hire_only: fleetReminder.during_hire_only || false,
      });

      // Find and set the selected stock item for display
      const stockItem = hireStockItems.find((item) => item.stock_no === fleetReminder.stock_no);
      setSelectedStockItem(stockItem || null);
    } else {
      // Reset for new fleet reminder
      setSelectedStockItem(null);
    }
  }, [fleetReminder, hireStockItems]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
    if (validationErrors[e.target.name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleStockSelect = (e) => {
    const selectedStockNo = e.target.value;
    const stockItem = hireStockItems.find((item) => item.stock_no === selectedStockNo);

    setFormData({
      ...formData,
      stock_no: selectedStockNo,
    });
    setSelectedStockItem(stockItem || null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateFleetReminderForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      return;
    }
    setValidationErrors({});
    setSaveError('');
    onSave(formData);
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {fleetReminder ? 'Edit Fleet Reminder' : 'Add New Fleet Reminder'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {saveError && (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Container fluid>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stock No *</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('stock_no')}>
                        Used for hire contract creation, availability tracking, and equipment
                        management
                      </Tooltip>
                    }
                  >
                    <Form.Select
                      name="stock_no"
                      value={formData.stock_no}
                      onChange={handleStockSelect}
                      required
                      {...createTooltipProps('stock_no')}
                    >
                      <option value="">Select hire stock item...</option>
                      <MissingSelectOption
                        value={formData.stock_no}
                        options={hireStockItems}
                        getOptionValue={(item) => item.stock_no}
                      />
                      {hireStockItems.map((item, index) => (
                        <option key={index} value={item.stock_no}>
                          {item.name} ({item.stock_no})
                        </option>
                      ))}
                    </Form.Select>
                  </OverlayTrigger>
                  {selectedStockItem && (
                    <Form.Text className="text-muted">
                      Stock No: {selectedStockItem.stock_no}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Service Name *</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('service_name')}>
                        Organizes reminders by type for different maintenance activities
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="text"
                      name="service_name"
                      value={formData.service_name}
                      onChange={handleChange}
                      maxLength="100"
                      required
                      {...createTooltipProps('service_name')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('due_date')}>
                        Used for scheduling and alert generation. Either Due Date or Due Meter is
                        required.
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleChange}
                      isInvalid={!!validationErrors.due_date}
                      {...createTooltipProps('due_date')}
                    />
                  </OverlayTrigger>
                  {validationErrors.due_date && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.due_date}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Due Meter</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('due_meter')}>
                        Used for scheduling and alert generation based on equipment usage. Either
                        Due Date or Due Meter is required.
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      name="due_meter"
                      value={formData.due_meter}
                      onChange={handleChange}
                      isInvalid={!!validationErrors.due_meter}
                      {...createTooltipProps('due_meter')}
                    />
                  </OverlayTrigger>
                  {validationErrors.due_meter && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.due_meter}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Notes</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('due_notes')}>
                        Provides specific information about the required action
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="due_notes"
                      value={formData.due_notes}
                      onChange={handleChange}
                      maxLength="200"
                      {...createTooltipProps('due_notes')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Inspection Name</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('inspection_name')}>
                        Used in reminder categorization and scheduling systems
                      </Tooltip>
                    }
                  >
                    <Form.Select
                      name="inspection_name"
                      value={formData.inspection_name}
                      onChange={handleChange}
                      {...createTooltipProps('inspection_name')}
                    >
                      <option value="">Select an inspection...</option>
                      <MissingSelectOption
                        value={formData.inspection_name}
                        options={inspectionNames.map((name) => ({ name }))}
                        getOptionValue={(item) => item.name}
                      />
                      {inspectionNames.map((name, index) => (
                        <option key={index} value={name}>
                          {name}
                        </option>
                      ))}
                    </Form.Select>
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Last Test Date</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('last_test_date')}>
                        Used for tracking previous maintenance and scheduling
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="date"
                      name="last_test_date"
                      value={formData.last_test_date}
                      onChange={handleChange}
                      {...createTooltipProps('last_test_date')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Last Test No</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('last_test_no')}>
                        Used for tracking previous maintenance and scheduling
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      type="text"
                      name="last_test_no"
                      value={formData.last_test_no}
                      onChange={handleChange}
                      maxLength="50"
                      {...createTooltipProps('last_test_no')}
                    />
                  </OverlayTrigger>
                </Form.Group>

                <Form.Group className="mb-3">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip {...createTooltipProps('during_hire_only')}>
                        Allows deactivation of reminders without deletion
                      </Tooltip>
                    }
                  >
                    <Form.Check
                      type="checkbox"
                      name="during_hire_only"
                      label="During Hire Only"
                      checked={formData.during_hire_only}
                      onChange={handleChange}
                      {...createTooltipProps('during_hire_only')}
                    />
                  </OverlayTrigger>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {fleetReminder ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function FleetReminders() {
  const [fleetReminders, setFleetReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFleetReminder, setEditingFleetReminder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [renderError, setRenderError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Error boundary for rendering errors
  useEffect(() => {
    const handleError = (error) => {
      console.error('Fleet Reminders render error:', error);
      setRenderError(error.message);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const fetchFleetReminders = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      console.log('Fetching fleet reminders...', { page, search });

      const response = await axios.get('/api/fleet-reminders', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });

      console.log('Fleet reminders response:', response.data);

      if (response.data && response.data.fleetReminders) {
        setFleetReminders(response.data.fleetReminders);
        setPagination(response.data.pagination);
      } else {
        console.error('Invalid response structure:', response.data);
        setFleetReminders([]);
        setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Error fetching fleet reminders:', error);
      setError(`Failed to fetch fleet reminders: ${error.message}`);
      setFleetReminders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleetReminders();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFleetReminders(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchFleetReminders(newPage, searchTerm);
  };

  const handleAddFleetReminder = () => {
    setEditingFleetReminder(null);
    setShowModal(true);
  };

  const handleEditFleetReminder = (fleetReminder) => {
    setEditingFleetReminder(fleetReminder);
    setShowModal(true);
  };

  const handleSaveFleetReminder = async (fleetReminderData) => {
    try {
      if (editingFleetReminder) {
        await axios.put(`/api/fleet-reminders/${editingFleetReminder.id}`, fleetReminderData);
        setSuccess('Fleet reminder updated successfully');
      } else {
        await axios.post('/api/fleet-reminders', fleetReminderData);
        setSuccess('Fleet reminder created successfully');
      }
      setShowModal(false);
      fetchFleetReminders(pagination.page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save fleet reminder');
    }
  };

  const handleDeleteFleetReminder = async (id) => {
    if (window.confirm('Are you sure you want to delete this fleet reminder?')) {
      try {
        await axios.delete(`/api/fleet-reminders/${id}`);
        setSuccess('Fleet reminder deleted successfully');
        fetchFleetReminders(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete fleet reminder');
      }
    }
  };

  const handleDeleteAllFleetReminders = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL fleet reminders? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your fleet reminder records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/fleet-reminders');
          setSuccess(
            `All fleet reminders deleted successfully (${response.data.deletedCount} records)`
          );
          fetchFleetReminders(1, '');
        } catch (error) {
          setError('Failed to delete all fleet reminders');
        }
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const handleCellEdit = (reminderId, field, currentValue) => {
    setEditingCell(`${reminderId}-${field}`);
    setEditingValue(currentValue || '');
  };

  const handleCellSave = async (reminderId, field) => {
    try {
      const reminder = fleetReminders.find((r) => r.id === reminderId);
      const updatedData = {
        ...reminder,
        [field]: editingValue,
      };

      await axios.put(`/api/fleet-reminders/${reminderId}`, updatedData);

      // Update local state
      setFleetReminders((prev) =>
        prev.map((r) => (r.id === reminderId ? { ...r, [field]: editingValue } : r))
      );

      setEditingCell(null);
      setEditingValue('');
      setSuccess(`${field === 'due_date' ? 'Due Date' : 'Due Meter'} updated successfully`);
    } catch (error) {
      setError(`Failed to update ${field === 'due_date' ? 'due date' : 'due meter'}`);
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleKeyPress = (e, reminderId, field) => {
    if (e.key === 'Enter') {
      handleCellSave(reminderId, field);
    } else if (e.key === 'Escape') {
      handleCellCancel();
    }
  };

  // Handle render errors
  if (renderError) {
    return (
      <Container fluid>
        <div className="alert alert-danger">
          <h4>Component Error</h4>
          <p>Fleet Reminders component failed to render: {renderError}</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              setRenderError(null);
              window.location.reload();
            }}
          >
            Reload Page
          </button>
        </div>
      </Container>
    );
  }

  try {
    return (
      <Container fluid>
        <Row className="mt-0 mb-2">
          <Col>
            <div className="d-flex align-items-center">
              <i className="fas fa-bell me-3 text-warning" style={{ fontSize: '2.5rem' }}></i>
              <div>
                <h1 className="mb-0">Fleet Reminders</h1>
                <p className="text-muted mb-0">
                  Manage maintenance and service reminders for fleet assets
                </p>
              </div>
            </div>
          </Col>
          <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
            <Button variant="primary" onClick={handleAddFleetReminder} className="me-2">
              Add Fleet Reminder
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAllFleetReminders}
              disabled={fleetReminders.length === 0}
            >
              Delete All
            </Button>
          </Col>
        </Row>

        {error && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError('')}
                  aria-label="Close"
                ></button>
              </div>
            </Col>
          </Row>
        )}

        {success && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                {success}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess('')}
                  aria-label="Close"
                ></button>
              </div>
            </Col>
          </Row>
        )}

        <Card>
          <Card.Body>
            <Form onSubmit={handleSearch} className="mb-3">
              <Row>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Search by stock no, service name, or notes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
                <Col xs="auto">
                  <Button variant="primary" type="submit">
                    Search
                  </Button>
                </Col>
              </Row>
            </Form>

            {loading ? (
              <div className="text-center p-3">Loading fleet reminders...</div>
            ) : fleetReminders.length === 0 ? (
              <div className="text-center p-3">No fleet reminders found.</div>
            ) : (
              <>
                <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                  <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                    <thead>
                      <tr>
                        <th>Stock No</th>
                        <th>Service Name</th>
                        <th>Due Date</th>
                        <th>Due Meter</th>
                        <th>Notes</th>
                        <th>Inspection</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fleetReminders.map((reminder) => (
                        <tr
                          key={reminder.id}
                          style={{
                            backgroundColor: isOverdue(reminder.due_date)
                              ? '#fff5f5'
                              : 'transparent',
                          }}
                        >
                          <td>
                            <strong>{reminder.stock_no}</strong>
                          </td>
                          <td>{reminder.service_name}</td>
                          <td
                            style={{
                              color: isOverdue(reminder.due_date) ? '#dc3545' : 'inherit',
                              fontWeight: isOverdue(reminder.due_date) ? 'bold' : 'normal',
                            }}
                          >
                            {editingCell === `${reminder.id}-due_date` ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input
                                  type="date"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onKeyDown={(e) => handleKeyPress(e, reminder.id, 'due_date')}
                                  onBlur={() => handleCellSave(reminder.id, 'due_date')}
                                  autoFocus
                                  style={{ width: '140px', fontSize: '12px', padding: '2px' }}
                                />
                                <button
                                  onClick={() => handleCellSave(reminder.id, 'due_date')}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '2px',
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '2px',
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() =>
                                  handleCellEdit(
                                    reminder.id,
                                    'due_date',
                                    reminder.due_date ? reminder.due_date.split('T')[0] : ''
                                  )
                                }
                                style={{
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '2px',
                                  minHeight: '20px',
                                  border: '1px solid transparent',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                  e.target.style.border = '1px solid #dee2e6';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.border = '1px solid transparent';
                                }}
                                title="Click to edit"
                              >
                                {formatDate(reminder.due_date)}
                                {isOverdue(reminder.due_date) && (
                                  <span style={{ marginLeft: '5px' }}>⚠️</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            {editingCell === `${reminder.id}-due_meter` ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onKeyDown={(e) => handleKeyPress(e, reminder.id, 'due_meter')}
                                  onBlur={() => handleCellSave(reminder.id, 'due_meter')}
                                  autoFocus
                                  style={{ width: '100px', fontSize: '12px', padding: '2px' }}
                                />
                                <button
                                  onClick={() => handleCellSave(reminder.id, 'due_meter')}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '2px',
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '2px',
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() =>
                                  handleCellEdit(reminder.id, 'due_meter', reminder.due_meter)
                                }
                                style={{
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '2px',
                                  minHeight: '20px',
                                  border: '1px solid transparent',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                  e.target.style.border = '1px solid #dee2e6';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.border = '1px solid transparent';
                                }}
                                title="Click to edit"
                              >
                                {reminder.due_meter || '-'}
                              </div>
                            )}
                          </td>
                          <td>{reminder.due_notes || '-'}</td>
                          <td>{reminder.inspection_name || '-'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditFleetReminder(reminder)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteFleetReminder(reminder.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <GridPagination
                  page={pagination.page}
                  pages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </Card.Body>
        </Card>

        {showModal && (
          <FleetReminderModal
            fleetReminder={editingFleetReminder}
            onClose={() => setShowModal(false)}
            onSave={handleSaveFleetReminder}
          />
        )}
      </Container>
    );
  } catch (renderError) {
    console.error('Fleet Reminders render error:', renderError);
    return (
      <Container fluid>
        <div className="alert alert-danger">
          <h4>Render Error</h4>
          <p>Fleet Reminders component crashed during render: {renderError.message}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      </Container>
    );
  }
}

export default FleetReminders;
